import {
	Container,
	Typography,
	Box,
	Divider,
	Card,
	CardContent,
	Grid,
	Button,
	CircularProgress,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { TagBubble } from "../../components/TagBubble";
import { Tag, TagType } from "../../entities/customer/Tag";
import { TagService } from "../../services/customer/TagService";
import { Add as AddIcon } from "@material-ui/icons";
import { BackButton } from "../../components/BackButton";
import { CreateTagDialog } from "./CreateTagDialog";
import { DeleteTagDialog } from "./DeleteTagDialog";

export function ManageTagsPage() {
	const [tags, setTags] = useState<Tag[]>([]);
	const [loading, setLoading] = useState(true);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [selectedTagType, setSelectedTagType] = useState<TagType>("Customer");

	useEffect(() => {
		loadTags();
	}, []);

	const loadTags = async () => {
		setLoading(true);
		try {
			const result = await TagService.getAll();
			if (result.success && result.data) {
				setTags(result.data);
			} else {
				console.error("Failed to load tags:", result.error);
			}
		} catch (error) {
			console.error("Failed to load tags:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteTag = (tag: Tag) => {
		setTagToDelete(tag);
		setDeleteDialogOpen(true);
	};

	const confirmDeleteTag = async () => {
		if (!tagToDelete) return;

		try {
			const result = await TagService.delete(tagToDelete.id);
			if (result.success) {
				setTags(tags.filter((t) => t.id !== tagToDelete.id));
			} else {
				console.error("Failed to delete tag:", result.error);
			}
		} catch (error) {
			console.error("Failed to delete tag:", error);
		} finally {
			setDeleteDialogOpen(false);
			setTagToDelete(null);
		}
	};

	const cancelDeleteTag = () => {
		setDeleteDialogOpen(false);
		setTagToDelete(null);
	};

	const openCreateDialog = (type: TagType) => {
		setSelectedTagType(type);
		setCreateDialogOpen(true);
	};

	const handleTagCreated = (newTag: Tag) => {
		setTags([...tags, newTag]);
		setCreateDialogOpen(false);
	};

	// Filter tags by type
	const customerTags = tags.filter((tag) => tag.type === "Customer");
	const ticketTags = tags.filter((tag) => tag.type === "Driver Ticket");

	const renderTagSection = (title: string, tagList: Tag[], type: TagType) => (
		<Card style={{ marginBottom: 20 }}>
			<CardContent>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
					<Typography variant="h6">{title}</Typography>
                    <Button variant="outlined" color="primary" onClick={() => openCreateDialog(type)} aria-label={`Add ${type} Tag`}>
                        <AddIcon />
                        Add Tag
                    </Button>
				</Box>
				<Divider style={{ marginBottom: 16 }} />

				{tagList.length === 0 ? (
					<Typography variant="body2" color="textSecondary">
						No {type.toLowerCase()} tags found.
					</Typography>
				) : (
					<Grid container spacing={1}>
						{tagList.map((tag) => (
							<Grid item key={tag.id}>
								<TagBubble tag={tag} onDelete={handleDeleteTag} />
							</Grid>
						))}
					</Grid>
				)}
			</CardContent>
		</Card>
	);

	return (
		<Container maxWidth="lg">
			<Box my={4}>
				<Box mb={2}>
                    <Grid container alignItems="center">
                        <Grid item>
                            <BackButton />
                        </Grid>
                        <Grid item>
                            <Typography variant="h4">Manage Tags</Typography>
                        </Grid>
                    </Grid>
				</Box>
				<Typography variant="body1" color="textSecondary" paragraph>
					View and manage your customer and ticket tags.
				</Typography>

				{loading ? (
					<Box display="flex" justifyContent="center" my={4}>
						<CircularProgress />
					</Box>
				) : (
					<>
						{renderTagSection("Customer Tags", customerTags, "Customer")}
						{renderTagSection("Ticket Tags", ticketTags, "Driver Ticket")}
					</>
				)}
			</Box>

			{/* Delete Confirmation Dialog */}
			<DeleteTagDialog open={deleteDialogOpen} onClose={cancelDeleteTag} tag={tagToDelete} onConfirm={confirmDeleteTag} />

			{/* Create Tag Dialog */}
			<CreateTagDialog open={createDialogOpen} type={selectedTagType} onClose={() => setCreateDialogOpen(false)} onTagCreated={handleTagCreated} />
		</Container>
	);
}
